import { useState, useEffect, useCallback, useSyncExternalStore } from 'react';
import { useSip } from './useSip';
import { getConnectionState, subscribe as mqttSubscribe } from '../services/MqttTelemetry';

export type ConnectivityLayer = 'network' | 'internet' | 'api' | 'sip' | 'mqtt';
export type ConnectivityStatus = 'connected' | 'degraded' | 'disconnected' | 'checking';

interface LayerStatus {
  network: boolean;
  internet: boolean;
  api: boolean;
  sip: boolean;
  mqtt: boolean;
}

const apiBaseUrl = (import.meta as any).env?.VITE_API_URL || 'https://api.caro.care';

export function useConnectivity() {
  const { sipState } = useSip();
  const mqttState = useSyncExternalStore(mqttSubscribe, getConnectionState);
  const [layers, setLayers] = useState<LayerStatus>({
    network: navigator.onLine,
    internet: false,
    api: false,
    sip: false,
    mqtt: false,
  });
  const [overallStatus, setOverallStatus] = useState<ConnectivityStatus>('checking');

  // Layer 1: Network (event-driven)
  useEffect(() => {
    const handleOnline = () => setLayers((prev) => ({ ...prev, network: true }));
    const handleOffline = () => setLayers((prev) => ({ ...prev, network: false }));
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Layer 2+3: Internet + API check (polling)
  const checkConnectivity = useCallback(async () => {
    // Layer 2: Internet access — test with a lightweight external fetch
    // We try multiple public endpoints to avoid false negatives from a single CDN issue
    let internetOk = false;
    try {
      const res = await fetch('https://dns.google/resolve?name=api.caro.care&type=A', {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      internetOk = res.ok;
    } catch {
      // Fallback: try a simple HEAD to a public CDN
      try {
        const res = await fetch('https://cloudflare.com/cdn-cgi/trace', {
          method: 'HEAD',
          mode: 'no-cors',
          signal: AbortSignal.timeout(5000),
        });
        // no-cors returns opaque response (status 0) but doesn't throw if reachable
        internetOk = true;
      } catch {
        internetOk = false;
      }
    }

    // Layer 3: CARO API — always check independently from internet
    let apiOk = false;
    try {
      const res = await fetch(`${apiBaseUrl}/api/health`, {
        signal: AbortSignal.timeout(5000),
      });
      apiOk = res.ok;
    } catch {
      apiOk = false;
    }

    // If API is reachable, internet must be working too
    if (apiOk) internetOk = true;

    setLayers((prev) => ({ ...prev, internet: internetOk, api: apiOk }));
  }, []);

  useEffect(() => {
    checkConnectivity();
    const interval = setInterval(checkConnectivity, 30_000);
    return () => clearInterval(interval);
  }, [checkConnectivity]);

  // Layer 4: SIP registration (driven by useSip singleton)
  useEffect(() => {
    setLayers((prev) => ({ ...prev, sip: sipState === 'registered' }));
  }, [sipState]);

  // Layer 5: MQTT telemetry connection
  useEffect(() => {
    setLayers((prev) => ({ ...prev, mqtt: mqttState === 'connected' }));
  }, [mqttState]);

  // Calculate overall status
  useEffect(() => {
    if (!layers.network) {
      setOverallStatus('disconnected');
    } else if (!layers.api || !layers.sip) {
      setOverallStatus('degraded');
    } else {
      setOverallStatus('connected');
    }
  }, [layers]);

  return { layers, overallStatus, checkConnectivity };
}
